import React, { memo, useCallback, useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import {
  setTimer,
  setImageProcessing,
  setTimerDone,
  setCanvasDimension,
  setInnerModelDimension,
  setIsDrawingStarted,
  setBrushSize,
  setIsInteractivePage,
} from "../../../state/actions";
import { Loader } from "../../loader/Loader";
import { calcCanvasAndModelDim } from "../../../lib/CalcCanvasAndModelDim";
// import { throttle } from "../../../lib/Throttle";
import { preventDefaultMouseTouchEvents } from "../../../lib/preventDefaultMouseTouchEvents";
import DrawingResult from "../DrawingResult/DrawingResult";
import CanvasStartOverlay from "../CanvasStartOverlay/CanvasStartOverlay";
import "./canvas.style.scss";
import { Model } from "../../model/DrawingModel/DrawingModel";
import { HOME_DRAWING_GIVEN_TIME } from "../../../templates/home";
import { setModelViewDimension } from "../../../state/actions";
import { Capacitor } from "@capacitor/core";
import isMobile from "ismobilejs";
import CanvasDrawingToolbar from "../CanvasDrawingToolbar/CanvasDrawingToolbar";
import CanvasSubmitControllerToolbar from "../CanvasSubmitControllerToolbar/CanvasSubmitContollerToolbar";
import { Button } from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import CursorIndicator from "../CursorIndicator/CursorIndicator";

const platform = Capacitor.getPlatform();

export const CANVAS_CLASS = "canvas";
export const CANVAS_ID = "drawing-canvas";
const styles: any = {
  canvas: {
    cursor: "crosshair",
    backgroundColor: "#fff",
    position: "absolute",
  },
};

type Props = {
  timerDone: boolean;
  shouldResetCanvas: boolean;
  setIsDrawingStarted: (bool: boolean) => void;
  isDrawingStarted: boolean;
  setShouldResetCanvas: (bool: boolean) => void;
  leftHand: boolean;
  setTimerDone: (bool: boolean) => void;
  setTimer: ({ showTimer, timer }: any) => void;
  canvasWidth: number;
  setCanvasDimension: ({ canvasWidth, canvasHeight }: any) => void;
  setInnerModelDimension: ({ innerModelWidth, innerModelHeight }: any) => void;
  setHasUserDrawnOnCanvas: (bool: boolean) => void;
  canStartDrawing: boolean;
  isInHomePage: boolean;
  canvasHeight: number;
  imageProcessing: boolean;
  isCompeting: boolean;
  canJoinClub: boolean;
  activeModel: {
    isDrawn: boolean;
  };
  navigateToClubPage: () => void;
  imgPath: string;
  model: Model;
  baseURL?: string;
  setBaseUrl: (baseURL: string) => void;
  brushSize: number;
  homeDrawingGivenTime: number;
  setBrushSize: (payload) => void;
  setIsInteractivePage: (payload) => void;
  splitRatio: number;
};
const ERASER_TO_BRUSH_SIZE_RATIO = 5;

const Canvas = memo((props: Props & any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_countDown, setCountDown] = useState(props.homeDrawingGivenTime);
  const [fadeOut, setFadeOut] = useState(false);
  const [lineColor] = useState("black");
  const [lineWidth, setLineWidth] = useState(props.brushSize);
  const [soloStartOverlayText, setSoloStartOverlayText] = useState(
    "When you are all set with the model, start by drawing here and the timer begins automatically."
  );
  const [eraserEnabled, setEraserEnabled] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isCursorVisible, setIsCursorVisible] = useState(false);
  const [cursorSize, setCursorSize] = useState(props.brushSize);

  const strokeHistory = useRef([]);
  const context = useRef(null);
  const canvasRef = useRef(null);
  const CANVAS_WIDTH = props.canvasWidth;
  const CANVAS_HEIGHT = props.canvasHeight;

  const isMousedown = useRef(false);
  const points = useRef([]);

  useEffect(() => {
    setCountDown(props.homeDrawingGivenTime);
  }, [props.homeDrawingGivenTime]);

  const handleEraserEnabled = (bool: boolean) => {
    setEraserEnabled((prev) => {
      isMousedown.current = false;
      if (prev) {
        // Switching from eraser to pen
        setLineWidth(props.brushSize);
        setCursorSize(props.brushSize);
      } else {
        // Switching from pen to eraser
        setLineWidth(props.brushSize * ERASER_TO_BRUSH_SIZE_RATIO);
      }
      return bool;
    });
  };

  useEffect(() => {
    setEraserEnabled(false);
    setLineWidth(props.brushSize);
    setCursorSize(props.brushSize * ERASER_TO_BRUSH_SIZE_RATIO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDrawingStarted]);

  useEffect(() => {
    if (!props.isDrawingStarted || props.imageProcessing) {
      strokeHistory.current = [];
      points.current = [];
      setHasStrokes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDrawingStarted, props.imageProcessing]);

  const drawOnCanvas = useCallback(
    (stroke) => {
      const ctx = context.current;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const l = stroke.length - 1;
      if (stroke.length >= 3) {
        const xc = (stroke[l].x + stroke[l - 1].x) / 2;
        const yc = (stroke[l].y + stroke[l - 1].y) / 2;
        ctx.lineWidth = stroke[l - 1].lineWidth;
        ctx.strokeStyle = stroke[l - 1].color;
        ctx.quadraticCurveTo(stroke[l - 1].x, stroke[l - 1].y, xc, yc);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(xc, yc);
      } else {
        const point = stroke[l];
        ctx.lineWidth = point.lineWidth;
        ctx.strokeStyle = point.color;
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
        ctx.stroke();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props]
  );

  useEffect(() => {
    if (canvasRef?.current) {
      strokeHistory.current = [];
      canvasRef.current.width = CANVAS_WIDTH;
      canvasRef.current.height = CANVAS_HEIGHT;
      context.current = canvasRef.current.getContext("2d");
      context.current.fillStyle = "white";
      context.current.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT); // Ensure the canvas is cleared
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef?.current, CANVAS_WIDTH, CANVAS_HEIGHT]);

  const getCoordinates = (e) => {
    let x,
      y,
      pressure = 0.1;
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      if (typeof touch.force !== "undefined" && touch.force > 0) {
        pressure = touch.force;
      }
      const rect = canvasRef.current.getBoundingClientRect();
      x = (touch.clientX - rect.left) * (CANVAS_WIDTH / rect.width);
      y = (touch.clientY - rect.top) * (CANVAS_HEIGHT / rect.height);
    } else {
      pressure = 1.0;
      const rect = canvasRef.current.getBoundingClientRect();
      x = (e.clientX - rect.left) * (CANVAS_WIDTH / rect.width);
      y = (e.clientY - rect.top) * (CANVAS_HEIGHT / rect.height);
    }
    return { x, y, pressure };
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!props.isDrawingStarted || props.imageProcessing) return;

    const rect = canvasRef.current.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      const touch = e.touches[0];
      x = touch.clientX;
      y = touch.clientY;
    } else {
      x = e.clientX;
      y = e.clientY;
    }

    // Convert to canvas coordinates
    const canvasX = (x - rect.left) * (CANVAS_WIDTH / rect.width);
    const canvasY = (y - rect.top) * (CANVAS_HEIGHT / rect.height);

    setCursorPosition({ x: canvasX, y: canvasY });
    // Only show cursor when eraser is enabled and actively drawing
    setIsCursorVisible(eraserEnabled && isMousedown.current);
  };

  const startDrawing = (e) => {
    if (props.canStartDrawing && !props.imageProcessing) {
      !props.isDrawingStarted && props.setIsDrawingStarted(true);

      if (props.timerDone) {
        if (props.isInHomePage) {
          props.setTimer({
            showTimer: true,
            timer: props.homeDrawingGivenTime,
          });
          props.setTimerDone(false);
        } else {
          props.setHasUserDrawnOnCanvas(true);
        }
        setFadeOut(true);
      }

      const { x, y } = getCoordinates(e);

      isMousedown.current = true;
      context.current.lineWidth = lineWidth || props.brushSize;
      context.current.strokeStyle = lineColor;
      context.current.globalCompositeOperation = "source-over";
      points.current.push({
        x,
        y,
        lineWidth: context.current.lineWidth,
        color: eraserEnabled ? "white" : lineColor,
      });
      drawOnCanvas(points.current);

      // Show cursor only if eraser is enabled
      setIsCursorVisible(eraserEnabled);
    }
  };

  const continueDrawing = (e) => {
    if (!isMousedown.current) return;
    e.preventDefault();

    const { x, y, pressure } = getCoordinates(e);
    const lineAmplifier = eraserEnabled ? 10 * ERASER_TO_BRUSH_SIZE_RATIO : 10;
    const newLineWidth =
      Math.log(pressure + 1) * (props.brushSize * lineAmplifier) * 0.2 +
      lineWidth * 0.8;

    setLineWidth(newLineWidth);
    // Update cursor size based on pressure
    const newCursorSize =
      Math.log(pressure + 1) * (props.brushSize * lineAmplifier) * 0.2 +
      cursorSize * 0.8;
    setCursorSize(newCursorSize);

    points.current.push({
      x,
      y,
      lineWidth: newLineWidth,
      color: eraserEnabled ? "white" : lineColor,
    });

    drawOnCanvas(points.current);
  };

  const stopDrawing = () => {
    isMousedown.current = false;
    setIsCursorVisible(false); // Hide cursor when stopping drawing
    // Reset cursor size to original brush size
    setCursorSize(
      eraserEnabled
        ? props.brushSize * ERASER_TO_BRUSH_SIZE_RATIO
        : props.brushSize
    );

    (
      window.requestIdleCallback ||
      function (cb) {
        setTimeout(cb, 1);
      }
    )(function () {
      if (points.current.length > 0) {
        strokeHistory.current.push([...points.current]);
        setHasStrokes(true);
      }
      points.current = [];
    });
  };

  function undoDraw() {
    if (strokeHistory.current.length === 0) return;

    strokeHistory.current.pop();
    setHasStrokes(strokeHistory.current.length > 0);

    context.current.clearRect(0, 0, canvasWidth, canvasHeight);
    context.current.fillStyle = "white";
    context.current.fillRect(0, 0, canvasWidth, canvasHeight);

    strokeHistory.current.forEach((stroke) => {
      if (!stroke || stroke.length === 0) return;

      for (let i = 0; i < stroke.length; i++) {
        const point = stroke[i];
        if (i === 0) {
          context.current.beginPath();
          context.current.moveTo(point.x, point.y);
          context.current.lineWidth = point.lineWidth;
          context.current.strokeStyle = point.color;
        } else if (i === stroke.length - 1) {
          const xc = (point.x + stroke[i - 1].x) / 2;
          const yc = (point.y + stroke[i - 1].y) / 2;
          context.current.quadraticCurveTo(
            stroke[i - 1].x,
            stroke[i - 1].y,
            xc,
            yc
          );
          context.current.stroke();
        } else {
          const xc = (point.x + stroke[i - 1].x) / 2;
          const yc = (point.y + stroke[i - 1].y) / 2;
          context.current.quadraticCurveTo(
            stroke[i - 1].x,
            stroke[i - 1].y,
            xc,
            yc
          );
          context.current.stroke();
          context.current.beginPath();
          context.current.moveTo(xc, yc);
        }
      }
    });
  }

  const setCanvasSize = useCallback(() => {
    const dims = calcCanvasAndModelDim(() => {
      styles.canvas = {
        ...styles.canvas,
        marginRight: "0",
      };
    });
    const screenWidth =
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      window.innerWidth;
    const screenHeight =
      document.documentElement.clientHeight ||
      document.body.clientHeight ||
      window.innerHeight;
    const isWrapped = screenWidth < screenHeight;
    const ratio = Math.max(0.25, Math.min(0.75, props.splitRatio || 0.5));
    let modelViewWidth: number;
    let canvasWidth: number;
    let canvasHeight: number;
    let modelViewHeight: number;
    if (isWrapped) {
      const availableHeight = screenHeight; // overlay splitter, no gap
      modelViewHeight = Math.floor(availableHeight * ratio);
      canvasHeight = Math.floor(availableHeight - modelViewHeight);
      modelViewWidth = screenWidth;
      canvasWidth = screenWidth;
    } else {
      const availableWidth = screenWidth; // overlay splitter, no gap
      modelViewWidth = Math.floor(
        availableWidth * (props.leftHand ? 1 - ratio : ratio)
      );
      canvasWidth = Math.floor(availableWidth - modelViewWidth);
      canvasHeight = screenHeight;
      modelViewHeight = screenHeight;
    }
    const { innerModelWidth, innerModelHeight } = dims;
    props.setCanvasDimension({
      canvasWidth,
      canvasHeight,
    });
    props.setModelViewDimension({
      modelViewWidth,
      modelViewHeight,
    });
    props.setInnerModelDimension({
      innerModelWidth,
      innerModelHeight,
    });
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.canvasWidth, props.canvasHeight, props.leftHand, props.splitRatio]);

  const isMobileDevice = () => {
    return isMobile(window.navigator).any;
  };

  const reset = useCallback(() => {
    const ratio = props.canvasWidth && props.canvasWidth < 500 ? 1800 : 700;
    const newLineWidth = props.brushSize
      ? props.brushSize
      : ((isMobileDevice() ? 2 : platform === "web" ? 0.2 : 2) *
          props.canvasWidth) /
        ratio;
    props.setBrushSize(newLineWidth);
    setLineWidth(newLineWidth);

    const canvas = canvasRef.current;
    if (canvas && canvas.getContext) {
      const context = canvas.getContext("2d");
      context.fillStyle = "white";
      context.fillRect(0, 0, props.canvasWidth, props.canvasHeight);
      preventDefaultMouseTouchEvents(canvas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.canvasHeight, props.canvasWidth]);

  const handleBrushSizeChange = (lineWidth: number) => {
    setLineWidth(lineWidth);
  };

  useEffect(() => {
    props.setIsInteractivePage(true);
    return () => {
      props.setIsInteractivePage(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.activeModel.isDrawn) {
      setFadeOut(false);
      setSoloStartOverlayText("You can start drawing again!");
    }
  }, [props.activeModel.isDrawn]);

  useEffect(() => {
    const setCanvasSizeOnResize = () => {
      if (props.timerDone) {
        setCanvasSize();
      }
    };
    window.addEventListener("resize", setCanvasSizeOnResize);
    return () => {
      window.removeEventListener("resize", setCanvasSizeOnResize);
      props.setTimerDone(true);
      props.setTimer({ showTimer: false, timer: HOME_DRAWING_GIVEN_TIME });
      props.setIsDrawingStarted(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.shouldResetCanvas) {
      reset();
      props.setShouldResetCanvas(false);
    }
    if (props.activeModel.isDrawn) {
      props.setIsDrawingStarted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.shouldResetCanvas, props.activeModel.isDrawn, reset]);

  useEffect(() => {
    setCanvasSize();
  }, [props.leftHand, props.splitRatio, setCanvasSize]);

  useEffect(() => {
    setIsDrawingStarted(true);
    setCanvasSize();
  }, [props.canvasWidth, props.canvasHeight, setCanvasSize]);

  const { canvasWidth, canvasHeight, isCompeting } = props;
  const side = props.leftHand ? `${CANVAS_CLASS}--left-hand` : "";

  const handleMouseLeave = () => {
    setIsCursorVisible(false);
  };

  return (
    <>
      {!props.isDrawingResultShowing && (
        <CanvasDrawingToolbar
          eraserEnabled={eraserEnabled}
          setEraserEnabled={handleEraserEnabled}
          lineWidth={lineWidth}
          handleBrushSizeChange={handleBrushSizeChange}
          canvasWidth={canvasWidth}
        />
      )}

      {canvasWidth > 0 && canvasHeight > 0 && (
        <div
          className={CANVAS_CLASS + " " + side}
          style={{
            width: `${canvasWidth}px`,
            height: `${canvasHeight}px`,
            position: "relative",
          }}
        >
          {!isCompeting &&
            props.isDrawingStarted &&
            !props.isDrawingResultShowing && (
              <CanvasSubmitControllerToolbar resetCanvas={reset} />
            )}
          {props.imageProcessing && <Loader canvasBlocker={true} />}

          <CanvasStartOverlay
            canStartDrawing={props.canStartDrawing}
            isCompeting={isCompeting}
            fadeOut={fadeOut}
            soloStartOverlayText={soloStartOverlayText}
          />

          {props.baseURL && (
            <DrawingResult
              imgPath={props.imgPath}
              model={props.model}
              baseURL={props.baseURL}
              isInHomePage={false}
              isCompeting={props.isCompeting}
            />
          )}

          <div className="canvas-container">
            <div className="canvas-wrapper">
              {hasStrokes && (
                <>
                  <Button
                    className="undo-button"
                    onClick={undoDraw}
                    disabled={!props.isDrawingStarted}
                  >
                    <span className="undo-button-text">Undo</span>
                    <UndoIcon />
                  </Button>
                </>
              )}
              <canvas
                id={CANVAS_ID}
                style={{
                  ...styles.canvas,
                  width: `${canvasWidth}px`,
                  height: `${canvasHeight}px`,
                }}
                className={`${CANVAS_CLASS}__area`}
                width={`${canvasWidth}px`}
                height={`${canvasHeight}px`}
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={(e) => {
                  handleMouseMove(e);
                  continueDrawing(e);
                }}
                onMouseUp={stopDrawing}
                onMouseLeave={(e) => {
                  handleMouseLeave();
                  stopDrawing();
                }}
                onTouchStart={startDrawing}
                onTouchMove={(e) => {
                  handleMouseMove(e);
                  continueDrawing(e);
                }}
                onTouchEnd={stopDrawing}
              />
              <CursorIndicator
                size={cursorSize}
                isEraser={eraserEnabled}
                position={cursorPosition}
                visible={isCursorVisible && eraserEnabled}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
});

const mapStateToProps = (state: any) => {
  const {
    timer,
    showTimer,
    timerDone,
    imageProcessing,
    currentScore,
    leftHand,
    startImageProcessing,
    activeModel,
    canvasWidth,
    canvasHeight,
    isDrawingStarted,
    isDrawingResultShowing,
    brushSize,
    homeDrawingGivenTime,
  } = state;
  return {
    timer,
    showTimer,
    timerDone,
    imageProcessing,
    currentScore,
    leftHand,
    startImageProcessing,
    activeModel,
    canvasWidth,
    canvasHeight,
    isDrawingStarted,
    isDrawingResultShowing,
    brushSize,
    homeDrawingGivenTime,
    splitRatio: state.splitRatio,
  };
};
const mapDispatchToProps = (dispatch: any) => {
  return {
    setTimer: (payload: any) => dispatch(setTimer(payload)),
    setImageProcessing: (payload: any) => dispatch(setImageProcessing(payload)),
    setTimerDone: (payload: any) => dispatch(setTimerDone(payload)),
    setCanvasDimension: (payload: any) => dispatch(setCanvasDimension(payload)),
    setInnerModelDimension: (payload: any) =>
      dispatch(setInnerModelDimension(payload)),
    setModelViewDimension: (payload: any) =>
      dispatch(setModelViewDimension(payload)),
    setIsDrawingStarted: (payload: any) =>
      dispatch(setIsDrawingStarted(payload)),
    setBrushSize: (payload) => dispatch(setBrushSize(payload)),
    setIsInteractivePage: (payload) => dispatch(setIsInteractivePage(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Canvas);
